import React, { useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DatePickerModal } from '../../../components/common/DatePickerModal';
import api from '../../../services/api';
import styles from '../ReportMobile.module.css';

const RevenueReportMobile: React.FC = () => {
  const [range, setRange] = useState<[
    string,
    string
  ]>([
    dayjs().startOf('month').format('YYYY-MM-DD'),
    dayjs().endOf('month').format('YYYY-MM-DD')
  ]);
  const [loading, setLoading] = useState(false);
  const [sales, setSales] = useState<any[]>([]); // StockOutVoucher[]
  const [cogs, setCogs] = useState<any[]>([]); // StockInVoucher[]
  // Chi phí hoạt động từ bảng expenses
  const [operatingExpenses, setOperatingExpenses] = useState<any[]>([]);

  // Date picker modal states
  const [startDateModalOpen, setStartDateModalOpen] = useState(false);
  const [endDateModalOpen, setEndDateModalOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs>(dayjs().startOf('month'));
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs>(dayjs().endOf('month'));

  // Tổng hợp
  const totalRevenue = sales.reduce(
    (sum, v) => sum + v.items.reduce((s: number, i: any) => s + i.quantity * i.unit_price, 0),
    0
  );
  const totalCogs = cogs.reduce(
    (sum, v) => sum + v.items.reduce((s: number, i: any) => s + i.quantity * i.unit_price, 0),
    0
  );
  const grossProfit = totalRevenue - totalCogs;
  // Gom nhóm chi phí theo danh mục
  const expensesByCategory = operatingExpenses.reduce((acc: any, expense: any) => {
    const categoryName = expense.category_name || 'Không phân loại';
    if (!acc[categoryName]) {
      acc[categoryName] = {
        name: categoryName,
        total: 0
      };
    }
    acc[categoryName].total += expense.amount || 0;
    return acc;
  }, {});
  
  const totalOperatingExpenses = operatingExpenses.reduce((sum: number, e: any) => sum + (e.amount || 0), 0);
  const netProfit = grossProfit - totalOperatingExpenses;

  // Lấy danh sách phiếu xuất/nhập và chi tiết
  const fetchData = async () => {
    setLoading(true);
    try {
      const start_date = range[0];
      const end_date = range[1];
      
      // Sử dụng API mới để lấy tất cả dữ liệu trong 1 lần gọi
      const response = await api.get(`/api/reports/revenue-detail?start_date=${start_date}&end_date=${end_date}`);
      const data = response.data.data;
      
      setSales(data.sales || []);
      setCogs(data.cogs || []);
      
      // Lấy chi phí hoạt động từ bảng expenses
      try {
        const expensesResponse = await api.get(`/api/expenses?date_from=${start_date}&date_to=${end_date}&status=paid`);
        const expenses = expensesResponse.data || [];
        setOperatingExpenses(expenses);
      } catch (expenseErr) {
        console.error('Lỗi khi lấy chi phí:', expenseErr);
        setOperatingExpenses([]);
      }
    } catch (err) {
      alert('Lỗi khi lấy dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, []);

  // Handle date selection
  const handleStartDateSelect = (date: Dayjs) => {
    setSelectedStartDate(date);
    setRange([date.format('YYYY-MM-DD'), range[1]]);
  };

  const handleEndDateSelect = (date: Dayjs) => {
    setSelectedEndDate(date);
    setRange([range[0], date.format('YYYY-MM-DD')]);
  };

  return (
    <div className={styles.bgGray}>
      <div className={styles.stickyHeader}>
        <div className={styles.title}>Báo cáo doanh thu</div>
        <div className={styles.flexRow}>
          <button
            onClick={() => setStartDateModalOpen(true)}
            className={styles.inputDate}
            style={{ 
              background: '#fff', 
              border: '1px solid #ced4da', 
              borderRadius: '4px', 
              padding: '8px 12px',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
              color: '#333',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {selectedStartDate.format('DD/MM/YYYY')}
          </button>
          <span className={styles.textGray} style={{ alignSelf: 'center' }}>-</span>
          <button
            onClick={() => setEndDateModalOpen(true)}
            className={styles.inputDate}
            style={{ 
              background: '#fff', 
              border: '1px solid #ced4da', 
              borderRadius: '4px', 
              padding: '8px 12px',
              cursor: 'pointer',
              textAlign: 'left',
              width: '100%',
              color: '#333',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            {selectedEndDate.format('DD/MM/YYYY')}
          </button>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className={styles.button}
        >
          {loading ? 'Đang tải...' : 'Xem báo cáo'}
        </button>
      </div>
      <div style={{ padding: 8 }}>
        {/* I. Doanh thu bán hàng */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>I. Doanh Thu Bán Hàng</div>
          <div className={styles.tableWrapper}>
            <table className={styles.table} style={{ minWidth: 500 }}>
              <thead>
                <tr>
                  <th>Ngày</th>
                  <th>Số Phiếu</th>
                  <th>Tên Hàng</th>
                  <th>SL</th>
                  <th>Đơn Giá</th>
                  <th>Thành Tiền</th>
                </tr>
              </thead>
              <tbody>
                {sales.flatMap((voucher: any) =>
                  voucher.items.map((item: any, idx: number) => (
                    <tr key={voucher.id + '-' + item.id + '-' + idx} className={idx % 2 === 0 ? '' : styles.tableRowAlt}>
                      <td>{dayjs(voucher.created_at).format('DD/MM/YYYY')}</td>
                      <td>{voucher.voucher_number}</td>
                      <td>{item.medicine_name}</td>
                      <td>{item.quantity}</td>
                      <td>{item.unit_price.toLocaleString('vi-VN')}đ</td>
                      <td>{(item.quantity * item.unit_price).toLocaleString('vi-VN')}đ</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className={styles.sectionTotal}>
            Tổng Doanh Thu: {totalRevenue.toLocaleString('vi-VN')}đ
          </div>
        </div>
        {/* II. Giá vốn hàng bán */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>II. Giá Vốn Hàng Bán</div>
          <div className={styles.tableWrapper}>
            <table className={styles.table} style={{ minWidth: 400 }}>
              <thead>
                <tr>
                  <th>Tên Hàng</th>
                  <th>SL</th>
                  <th>Giá Vốn</th>
                  <th>Tổng Giá Vốn</th>
                </tr>
              </thead>
              <tbody>
                {cogs.flatMap((voucher: any) =>
                  voucher.items.map((item: any, idx: number) => (
                    <tr key={voucher.id + '-' + item.id + '-' + idx} className={idx % 2 === 0 ? '' : styles.tableRowAlt}>
                      <td>{item.medicine_name}</td>
                      <td>{item.quantity}</td>
                      <td>{item.unit_price.toLocaleString('vi-VN')}đ</td>
                      <td>{(item.quantity * item.unit_price).toLocaleString('vi-VN')}đ</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          <div className={styles.sectionTotal + ' ' + styles.sectionTotalLoss}>
            Tổng Giá Vốn: {totalCogs.toLocaleString('vi-VN')}đ
          </div>
        </div>
        {/* III. Lợi nhuận gộp */}
        <div className={styles.section}>
          <div className={styles.sectionTitle + ' ' + styles.sectionTitleProfit}>III. Lợi Nhuận Gộp</div>
          <div className={styles.sectionTotal + ' ' + styles.sectionTitleGross} style={{ fontSize: 18, fontWeight: 700 }}>
            {grossProfit.toLocaleString('vi-VN')}đ
          </div>
        </div>
        {/* IV. Chi phí hoạt động */}
        <div className={styles.section}>
          <div className={styles.sectionTitle}>IV. Chi Phí Hoạt Động</div>
          {operatingExpenses.length === 0 ? (
            <div className={styles.sectionNote}>
              (Chưa có dữ liệu chi phí hoạt động)
            </div>
          ) : (
            <div className={styles.tableWrapper}>
              <table className={styles.table} style={{ minWidth: 300 }}>
                <thead>
                  <tr>
                    <th>Danh Mục Chi Phí</th>
                    <th>Tổng Số Tiền</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.values(expensesByCategory).map((category: any, idx: number) => (
                    <tr key={category.name + '-' + idx} className={idx % 2 === 0 ? '' : styles.tableRowAlt}>
                      <td>{category.name}</td>
                      <td>{category.total.toLocaleString('vi-VN')}đ</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className={styles.sectionTotal + ' ' + styles.sectionTotalLoss}>
            Tổng Chi Phí: {totalOperatingExpenses.toLocaleString('vi-VN')}đ
          </div>
        </div>
        {/* V. Lợi nhuận ròng */}
        <div className={styles.section}>
          <div className={styles.sectionTitle + ' ' + styles.sectionTitleProfit}>V. Lợi Nhuận Ròng</div>
          <div className={styles.sectionTotalProfit}>
            {netProfit.toLocaleString('vi-VN')}đ
          </div>
        </div>
      </div>

      {/* Date Picker Modals */}
      <DatePickerModal
        isOpen={startDateModalOpen}
        onClose={() => setStartDateModalOpen(false)}
        onSelect={handleStartDateSelect}
        selectedDate={selectedStartDate}
        title="Chọn ngày bắt đầu"
      />
      <DatePickerModal
        isOpen={endDateModalOpen}
        onClose={() => setEndDateModalOpen(false)}
        onSelect={handleEndDateSelect}
        selectedDate={selectedEndDate}
        title="Chọn ngày kết thúc"
      />
    </div>
  );
};

export default RevenueReportMobile;